// core
import {
  Component, Input, OnInit, ViewChild, ElementRef
} from '@angular/core';
import { Subscription } from 'rxjs';
import { startWith } from 'rxjs/operators';

// campfire
import { PartnerBrandService } from '@campfire-nx/api';

// Raptor
import { RAPTOR_ARRANGEMENT_TAG } from '../../constant/raptor-constants';
import { RaptorStepDataService } from '../../services/raptor-step-data.service';
import { RaptorStepService } from '../../services/raptor-step.service';
import { RaptorPredicateService } from '../../services/raptor-predicate.service';
import { RaptorDataService, IArrangementFeeBanner } from '../../services/raptor-data.service';
import { RaptorPriceService } from '../../services/raptor-price.service';

interface IArrangFeeCharges {
  enabled: boolean;
  showBreakdown: boolean;
  breakdown: any;
  monthlyPayableFirstInvoice: string | null;
  banner: IArrangementFeeBanner | null;
}

interface price {
  currency?: string;
  amount?: number;
  frequency?: string | null
}

@Component({
  selector: 'open-raptor-total-price',
  templateUrl: './raptor-total-price.component.html',
  styleUrls: ['./raptor-total-price.component.scss']
})
export class RaptorTotalPriceComponent implements OnInit {
  @Input() componentDetails: any;
  @Input() form: any;
  @ViewChild('container') container!: ElementRef<HTMLInputElement>;

  formValueWatcher!: Subscription;
  currency!: string;
  arrangementTag!: string;
  price: price = {
    amount: 0,
    frequency: 'year'
  };
  priceDetails: Array<any> = [];
  priceCurrent: price = {
    frequency: 'year',
    amount: 0
  };
  priceNew: price = {
    frequency: 'year',
    amount: 0
  };
  priceDelta: price = {
    frequency: 'year',
    amount: 0
  };
  firstMonthPriceHeading?: string;
  precision: number = 2;
  heading?: string;
  subText?: string;
  banners?: any;
  disclaimers?: Array<any>;
  additionalCharges: IArrangFeeCharges = {
    enabled: false,
    showBreakdown: false,
    breakdown: null,
    monthlyPayableFirstInvoice: null,
    banner: null
  };

  constructor(
    private raptorStepDataService: RaptorStepDataService,
    public raptorStepService: RaptorStepService,
    private raptorPredicateService: RaptorPredicateService,
    private raptorDataService: RaptorDataService,
    private raptorPriceService: RaptorPriceService,
    public partnerBrandService: PartnerBrandService
  ) { }

  ngOnInit(): void {
    if (this.componentDetails.component === 'active_price') {
      this.calculateTotalPayable();
    }

    if (
      this.componentDetails.component === 'change_en_ca_price_component' ||
      this.componentDetails.component === 'new_rn_price'
    ) {
      this.calculateTotalChangePayable();
    }

    if (
      this.componentDetails.settings &&
      this.componentDetails.settings.precision === 'round_up'
    ) {
      this.precision = 0;
    }

    if (typeof this.componentDetails.heading === 'string' && this.componentDetails.heading.length > 0) {
      this.heading = this.componentDetails.heading;
    }
    if (typeof this.componentDetails.sub_text === 'string' && this.componentDetails.sub_text.length > 0) {
      this.subText = this.componentDetails.sub_text;
    }
    this.currency = this.partnerBrandService.getCurrencySymbol();
    this.arrangementTag = RAPTOR_ARRANGEMENT_TAG[this.partnerBrandService.getCountryCode('country_alpha2')];

    this.formValueWatcher = this.form.valueChanges
      .pipe(
        startWith(this.form.value)
      )
      .subscribe(() => {
        if (this.componentDetails.component === 'active_price') {
          this.calculateTotalPayable();
        }

        if (
          this.componentDetails.component === 'change_en_ca_price_component' ||
          this.componentDetails.component === 'new_rn_price'
        ) {
          this.calculateTotalChangePayable();
        }

        this.checkShowIf();
      });
  }

  ngAfterViewInit() {
  }

  ngOnDestroy(): void {
    if (this.formValueWatcher) {
      this.formValueWatcher.unsubscribe();
    }
  }

  checkShowIf(): void {
    if (this.componentDetails.heading && Array.isArray(this.componentDetails.heading)) {
      this.componentDetails.heading.forEach((item: any) => {
        if (item.show_if) {
          const showItem = this.raptorPredicateService.validator(item.show_if, this.form.value);
          if (showItem) {
            this.heading = item.value;
          }
        }
      });
    }

    if (this.componentDetails.sub_text && Array.isArray(this.componentDetails.sub_text)) {
      this.componentDetails.sub_text.forEach((item: any) => {
        if (item.show_if) {
          const showItem = this.raptorPredicateService.validator(item.show_if, this.form.value);
          if (showItem) {
            this.subText = item.value;
          }
        }
      });
    }
  }

  calculateTotalPayable() : void {
    const formData = this.raptorStepDataService.get();
    const formValues = this.form.value;
    let selectedCover;
    let frequency = formValues['frequency'] || formData.data['frequency'];
    let premium;

    if (formData.covers && Array.isArray(formData.covers)) {
      selectedCover = this.raptorDataService.getSelectedCover(formData, this.form);
      this.disclaimers = this.raptorDataService.mapBannerDisclaimers([selectedCover]);
    }
    
    if (selectedCover && frequency) {
      premium = selectedCover.premium[frequency];
      
      this.price = this.raptorPriceService.getTotalPrice(selectedCover, frequency, this.componentDetails.settings);
      this.price.currency = this.currency;

      if (this.componentDetails.settings.premium_breakdown) {
        this.priceDetails = this.raptorPriceService.getPriceBreakdown(selectedCover, frequency, this.currency);
      }

      if (selectedCover.banner && selectedCover.banner.length) {
        this.banners = selectedCover.banner;
      }
      
      // TODO: refactor this and put it in the price service
      if (selectedCover.has_additional_charges && selectedCover.has_upfront_charges) {
        const showBreakdown = this.componentDetails.settings.premium_breakdown;

        this.additionalCharges.enabled = true;
        this.additionalCharges.banner = this.raptorDataService.getArrangementFeeBanner(selectedCover.premium, this.disclaimers as any, frequency, !showBreakdown);
        this.additionalCharges.showBreakdown = showBreakdown;
        if (selectedCover.premium.M) {
          this.additionalCharges.breakdown = this.raptorPriceService.getMonthlyPaymentBreakdown(selectedCover.premium.M, this.currency);
        }
      } else {
        this.additionalCharges.enabled = true;
        this.additionalCharges.banner = this.raptorDataService.getArrangementFeeBanner(selectedCover.premium, this.disclaimers as any, frequency, false);
        this.additionalCharges.showBreakdown = false;
      }
    }
  }

  calculateTotalChangePayable(): void {
    let formData = this.raptorStepDataService.get();
    let formValues = this.form.value;
    let premium = formData.covers && formData.covers.premium;
    let frequency = formValues['frequency'] || formData.data['frequency'];

    // TODO: refactor this and put it in the price service
    if (frequency === 'U') {
      this.priceDelta.frequency = 'year';
    }

    if (frequency === 'M') {
      this.priceDelta.frequency = 'month';
    }

    if (this.componentDetails.settings.premium_attribute === 'payable') {
      this.priceDelta.frequency = 'year';
    }

    if (this.componentDetails.settings.premium_attribute === 'payable_monthly') {
      this.priceDelta.frequency = 'month';
    }

    if (premium) {
      this.priceCurrent.amount = parseFloat(premium.current_total_premium);
      this.priceNew.amount = parseFloat(premium.new_total_premium);
      this.priceDelta.amount = parseFloat(frequency === 'M' ? premium.payable_monthly : premium.payable);

      if (premium.first_monthly_payable) {
        this.firstMonthPriceHeading = `First month ${this.currency}${parseFloat(premium.first_monthly_payable)}, then`;
      } else {
        this.firstMonthPriceHeading = undefined;
      }

      if (
        this.componentDetails.settings &&
        this.componentDetails.settings.precision === 'round_up'
      ) {
        this.priceCurrent.amount = Math.ceil(parseFloat(premium.current_total_premium));
        this.priceNew.amount = Math.ceil(parseFloat(premium.new_total_premium));
        this.priceDelta.amount = Math.ceil(parseFloat(frequency === 'M' ? premium.payable_monthly : premium.payable));
      }
      this.priceCurrent.currency = this.currency;
      this.priceNew.currency = this.currency;
      this.priceDelta.currency = this.currency;

      // remove frequency label if it is null
      if (premium && premium.frequency_label === null) {
        this.priceCurrent.frequency = null;
        this.priceNew.frequency = null;
        this.priceDelta.frequency = null;
      }
    }
  }

  getTagLabel(banner: any) {
    return this.raptorDataService.mapBannerTag(banner, this.disclaimers);
  }
}
